/**
 * Scene transitions -- blend between two frame buffers with visual effects.
 *
 * TypeScript port of the Rust dd-ascii-anim transition module. The
 * SceneTransition takes an old and new FrameBuffer and produces an
 * intermediate blended buffer that animates from one scene to the next.
 *
 * Supported transition types:
 * - Dissolve: Random cells flip from old to new based on a noise map.
 * - Fade: Old scene darkens through a glyph ramp, then new scene brightens.
 * - WipeRight: Left-to-right column reveal.
 * - WipeDown: Top-to-bottom row reveal.
 * - TypewriterReveal: Left-to-right, top-to-bottom (like typing).
 *
 * No box-drawing characters -- only alphanumeric/symbol glyphs.
 */

import { Color, type Cell, FrameBuffer } from "./engine";

// ---------------------------------------------------------------------------
// TransitionType
// ---------------------------------------------------------------------------

/** The type of scene transition effect. */
export enum TransitionType {
  /** Random cells flip via a pre-generated noise map. */
  Dissolve = "Dissolve",
  /** Glyph ramp: old darkens (# * + : . ' ') then new brightens in reverse. */
  Fade = "Fade",
  /** Left-to-right column reveal. */
  WipeRight = "WipeRight",
  /** Top-to-bottom row reveal. */
  WipeDown = "WipeDown",
  /** Left-to-right, top-to-bottom -- like a typewriter printing the new scene. */
  TypewriterReveal = "TypewriterReveal",
}

// ---------------------------------------------------------------------------
// Fade ramp
// ---------------------------------------------------------------------------

/**
 * The glyph ramp used for fade transitions.
 *
 * Phase 1 (progress 0.0-0.5): old cell darkens through this sequence.
 * Phase 2 (progress 0.5-1.0): new cell brightens in reverse order.
 */
const FADE_RAMP: readonly string[] = ["#", "*", "+", ":", ".", " "];

// ---------------------------------------------------------------------------
// Noise map generation
// ---------------------------------------------------------------------------

/**
 * Generate a deterministic noise map for dissolve transitions.
 *
 * Uses an xorshift64 PRNG to fill a width*height array with values
 * in [0.0, 1.0).
 */
function generateNoiseMap(width: number, height: number): number[] {
  const total = width * height;
  const noise: number[] = Array.from({ length: total });
  let state = 0xdeadbeefcafebaben; // fixed seed for reproducibility

  for (let i = 0; i < total; i++) {
    // xorshift64
    state ^= (state << 13n) & 0xffffffffffffffffn;
    state ^= state >> 7n;
    state ^= (state << 17n) & 0xffffffffffffffffn;

    const val = Number(state) / Number(0xffffffffffffffffn);
    noise[i] = val;
  }

  return noise;
}

// ---------------------------------------------------------------------------
// Helper
// ---------------------------------------------------------------------------

/** Extract (ch, fg, bg) from an optional cell reference, defaulting to blank. */
function cellValues(cell: Cell | undefined): [string, Color, Color] {
  if (cell) {
    return [cell.ch, cell.fg, cell.bg];
  }
  return [" ", Color.white(), Color.black()];
}

// ---------------------------------------------------------------------------
// SceneTransition
// ---------------------------------------------------------------------------

/**
 * A scene transition effect between two frame buffers.
 *
 * Create with the constructor, call advance() each frame, then apply() to
 * blend old and new buffers into an output buffer.
 */
export class SceneTransition {
  /** The transition effect type. */
  private readonly _transitionType: TransitionType;
  /** Progress from 0.0 (all old) to 1.0 (all new). */
  private _progress = 0;
  /** Total frames for this transition. */
  private readonly durationFrames: number;
  /** Current frame index. */
  private _currentFrame = 0;
  /** Pre-generated noise values for dissolve (one per cell, row-major). */
  private readonly noiseMap: number[];
  /** Buffer width. */
  private readonly width: number;
  /** Buffer height. */
  private readonly height: number;

  /**
   * Create a new scene transition.
   *
   * The durationFrames controls how many advance() calls it takes to reach
   * completion. For dissolve transitions, a noise map is generated using a
   * deterministic PRNG.
   */
  constructor(
    transitionType: TransitionType,
    durationFrames: number,
    width: number,
    height: number,
  ) {
    this._transitionType = transitionType;
    this.durationFrames = Math.max(durationFrames, 1);
    this.width = width;
    this.height = height;
    this.noiseMap =
      transitionType === TransitionType.Dissolve ? generateNoiseMap(width, height) : [];
  }

  /**
   * Advance the transition by one frame.
   *
   * Progress moves linearly from 0.0 to 1.0 over durationFrames.
   */
  advance(): void {
    if (this._currentFrame < this.durationFrames) {
      this._currentFrame += 1;
      this._progress = Math.min(this._currentFrame / this.durationFrames, 1.0);
    }
  }

  /** Get the current progress (0.0 = all old, 1.0 = all new). */
  progress(): number {
    return this._progress;
  }

  /** Whether the transition has completed. */
  isComplete(): boolean {
    return this._currentFrame >= this.durationFrames;
  }

  /** Get the current frame index. */
  currentFrame(): number {
    return this._currentFrame;
  }

  /** Get the transition type. */
  transitionType(): TransitionType {
    return this._transitionType;
  }

  /**
   * Blend the old and new buffers based on current progress.
   *
   * Returns a new FrameBuffer with the blended result. Both input buffers
   * must have the same dimensions as the transition was created with;
   * mismatched cells are treated as blank.
   */
  apply(old: FrameBuffer, newBuf: FrameBuffer): FrameBuffer {
    const output = new FrameBuffer(this.width, this.height);

    for (let r = 0; r < this.height; r++) {
      for (let c = 0; c < this.width; c++) {
        const oldCell = old.get(r, c);
        const newCell = newBuf.get(r, c);

        let result: [string, Color, Color];

        switch (this._transitionType) {
          case TransitionType.Dissolve:
            result = this.applyDissolve(r, c, oldCell, newCell);
            break;
          case TransitionType.Fade:
            result = this.applyFade(oldCell, newCell);
            break;
          case TransitionType.WipeRight:
            result = this.applyWipeRight(c, oldCell, newCell);
            break;
          case TransitionType.WipeDown:
            result = this.applyWipeDown(r, oldCell, newCell);
            break;
          case TransitionType.TypewriterReveal:
            result = this.applyTypewriter(r, c, oldCell, newCell);
            break;
        }

        output.set(r, c, result[0], result[1], result[2]);
      }
    }

    return output;
  }

  /** Dissolve: flip cell when noise value is below progress. */
  private applyDissolve(
    row: number,
    col: number,
    oldCell: Cell | undefined,
    newCell: Cell | undefined,
  ): [string, Color, Color] {
    const idx = row * this.width + col;
    const noise = idx < this.noiseMap.length ? this.noiseMap[idx] : 0.5;

    if (noise < this._progress) {
      return cellValues(newCell);
    }
    return cellValues(oldCell);
  }

  /**
   * Fade: darken old through glyph ramp (phase 1), brighten new in reverse
   * (phase 2).
   */
  private applyFade(oldCell: Cell | undefined, newCell: Cell | undefined): [string, Color, Color] {
    const rampLen = FADE_RAMP.length;

    if (this._progress <= 0.5) {
      // Phase 1: darken old cell
      const phaseProgress = this._progress * 2.0; // 0.0 - 1.0 within phase 1
      const rampIdx = Math.min(Math.floor(phaseProgress * rampLen), rampLen - 1);

      const [, fg, bg] = cellValues(oldCell);
      return [FADE_RAMP[rampIdx], fg, bg];
    }

    // Phase 2: brighten new cell (reverse ramp)
    const phaseProgress = (this._progress - 0.5) * 2.0; // 0.0 - 1.0 within phase 2
    const rampIdxFromEnd = Math.min(Math.floor(phaseProgress * rampLen), rampLen - 1);
    // Reverse: start from space, end at original char
    const rampIdx = Math.max(rampLen - 1 - rampIdxFromEnd, 0);

    const [newCh, fg, bg] = cellValues(newCell);

    // At full progress (1.0), show the actual new cell
    if (rampIdx === 0) {
      return [newCh, fg, bg];
    }
    return [FADE_RAMP[rampIdx], fg, bg];
  }

  /** Wipe right: columns left of the threshold show new, right show old. */
  private applyWipeRight(
    col: number,
    oldCell: Cell | undefined,
    newCell: Cell | undefined,
  ): [string, Color, Color] {
    if (this.width === 0) {
      return cellValues(oldCell);
    }
    const threshold = this._progress * this.width;
    if (col < threshold) {
      return cellValues(newCell);
    }
    return cellValues(oldCell);
  }

  /** Wipe down: rows above the threshold show new, below show old. */
  private applyWipeDown(
    row: number,
    oldCell: Cell | undefined,
    newCell: Cell | undefined,
  ): [string, Color, Color] {
    if (this.height === 0) {
      return cellValues(oldCell);
    }
    const threshold = this._progress * this.height;
    if (row < threshold) {
      return cellValues(newCell);
    }
    return cellValues(oldCell);
  }

  /** Typewriter: linear left-to-right, top-to-bottom reveal. */
  private applyTypewriter(
    row: number,
    col: number,
    oldCell: Cell | undefined,
    newCell: Cell | undefined,
  ): [string, Color, Color] {
    const totalCells = this.width * this.height;
    if (totalCells === 0) {
      return cellValues(oldCell);
    }
    const cellIndex = row * this.width + col;
    const threshold = this._progress * totalCells;
    if (cellIndex < threshold) {
      return cellValues(newCell);
    }
    return cellValues(oldCell);
  }
}
